import type { Action } from 'redux';
import type { Task } from 'redux-saga';
import { eventChannel } from 'redux-saga';
import { call, put, take, takeLatest, takeEvery, delay, fork, cancel } from 'redux-saga/effects';

import { restClient } from 'config/store/restClient';
import { SOCKET_AUTH_API_URL } from 'constants/ApiConstants';
import type { Action as GenericAction } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';

import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { PING_DELAY, RECONNECT_DELAY, SOCKET_STATUS } from './constants';
import type { SendMessageAction, SubscribeTopicAction, UnsubscribeTopicAction } from './types/actions';

type TopicState = { status: (typeof SOCKET_STATUS)[keyof typeof SOCKET_STATUS] } & Record<string, unknown>;

let socket: MayBe<WebSocket>;

export const topics = new Map<string, TopicState>();

const waitForConnection = async () =>
  new Promise<void>((resolve, reject) => {
    if (socket) {
      if (socket.readyState === WebSocket.OPEN) {
        resolve();
      } else if (socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
        reject();
      } else {
        socket.addEventListener('open', () => resolve(), { once: true });
        socket.addEventListener('error', () => reject(), { once: true });
      }
    } else {
      setTimeout(async () => {
        try {
          await waitForConnection();
          resolve();
        } catch {
          reject();
        }
      }, 100); // Retry every 100ms
    }
  });

const initWebSocket = (token: string) =>
  eventChannel<Action>(emitter => {
    socket = new WebSocket(process.env.REACT_APP_WS_URL!.replace('{{TOKEN}}', token));

    socket.onopen = () => emitter(actions.connectionSuccess());

    socket.onerror = error => emitter(actions.socketError(error));

    socket.onclose = () => {
      emitter(actions.socketClosed());
    };

    socket.onmessage = message => {
      try {
        const response = JSON.parse(message.data);
        // Core action - Ping, Subscription, Unsubscription etc...
        if (response.statusCode) {
          const isFailure = response.statusCode !== 200;
          if (isFailure) {
            emitter(actions.socketError(response.result));
          } else if (response.result) {
            const [abstractCommand] = response.result.split(' ');
            emitter(actions.abstractInternalCommand(abstractCommand));
          }
        } else {
          // Topic message - S2Sent, Intervention delete, etc ...
          emitter(actions.putSocketMessage(response));
        }
      } catch (error) {
        emitter(actions.socketError(error as Error));
      }
    };

    return () => {
      // Unsubscribe
      socket?.close();
    };
  });

function* resubscribeToTopics() {
  yield take(actionTypes.CONNECTED);
  for (const [topic, payload] of topics) {
    if (payload.status === SOCKET_STATUS.CONNECTED) { // Only reconnect to topics that were connected
      yield put(actions.sendMessage({ topic, action: 'subscribe', ...payload }));
    }
  }
}

export function* initialize() {
  let retryCounter = 0;
  do {
    let subscribeTask: MayBe<Task>;
    try {
      const { data }: { data: { token: string } } = yield call(restClient.request, { method: 'POST', url: SOCKET_AUTH_API_URL });
      const channel: ReturnType<typeof initWebSocket> = yield call(initWebSocket, data.token);
      subscribeTask = yield fork(resubscribeToTopics);

      do {
        // Receive an action from the socket engine, and put it in the app
        const action: Action = yield take(channel);
        yield put(action);
        if (action.type === actionTypes.CLOSED) {
          yield delay(RECONNECT_DELAY);
          retryCounter = 0;
          break;
        }
      } while (true);
    } catch (error) {
      console.error('WebSocket connection error:', error);
      yield delay(RECONNECT_DELAY);
    } finally {
      retryCounter += 1;

      if (subscribeTask) {
        yield cancel(subscribeTask);
      }
    }
  } while (retryCounter <= 5);
}

const getTopic = ({ type }: GenericAction<unknown>): MayBe<string> => {
  const match = type.match(/TOPIC:([^/]+)/);
  return match?.[1];
};

function* prepareTopic(action: SubscribeTopicAction, topic: string) {
  const isConnected = topics.has(topic) && topics.get(topic)?.status === SOCKET_STATUS.CONNECTED;

  if (isConnected) {
    yield call(unsubscribeChannelWorker, actions.unsubscribe(topic, action.payload, action.meta));
  }
  topics.set(topic, { status: SOCKET_STATUS.CONNECTING, ...action.payload });
}

function* subscribeChannelWorker(action: SubscribeTopicAction) {
  if (socket instanceof WebSocket) {
    try {
      yield call(waitForConnection);
    } catch {
      // Socket was probably closed
      yield put(actions.connectSocket(undefined, action.meta));
      yield take(actionTypes.CONNECTED);
    }
  } else {
    yield put(actions.connectSocket(undefined, action.meta));
    yield take(actionTypes.CONNECTED);
  }

  const room = getTopic(action);
  if (room) {
    yield call(prepareTopic, action, room);
    yield put(actions.sendMessage({ topic: room, action: 'subscribe', ...action.payload }, action.meta));
    const { type: response } = yield take([actionTypes.SUBSCRIBED, actionTypes.ERROR]);

    if (response === actionTypes.ERROR) {
      topics.delete(room);
    } else {
      topics.set(room, { status: SOCKET_STATUS.CONNECTED, ...action.payload });
    }
  }
}

export function* unsubscribeChannelWorker(action: UnsubscribeTopicAction) {
  const room = getTopic(action);

  if (room && topics.has(room)) {
    try {
      yield call(waitForConnection);

      const payload = topics.get(room);
      yield put(actions.sendMessage({ topic: room, action: 'unsubscribe', ...payload }, action.meta));

      const { type: response } = yield take([actionTypes.UNSUBSCRIBED, actionTypes.ERROR]);
      if (response === actionTypes.UNSUBSCRIBED) {
        topics.delete(room);
      }
    } catch (error) {
      console.error('Error when unsubscribing websocket:', error);
    }
  }
}

const subscriberAction = ({ type }: Action): boolean => {
  const regExp = new RegExp(`${actionTypes.ROOT}/TOPIC:([^/]+)/SUBSCRIBE`);
  return regExp.test(type);
};

const unsubscriberAction = ({ type }: Action): boolean => {
  const regExp = new RegExp(`${actionTypes.ROOT}/TOPIC:([^/]+)/UNSUBSCRIBE`);
  return regExp.test(type);
};

const sendMessage = async ({ payload }: SendMessageAction) => {
  try {
    await waitForConnection();
  } catch { /* empty */ }

  if (socket) {
    socket.send(JSON.stringify(payload));
  }
};

/** @returns true if the socket is (or becomes) connected, false if it is closing/closed */
export const isSocketConnected = async () => {
  try {
    await waitForConnection();
    return true;
  } catch {
    return false;
  }
};

export function* ping() {
  let isConnected: boolean = yield call(isSocketConnected);
  while (isConnected) {
    yield delay(PING_DELAY);
    yield put(actions.sendMessage({ action: 'ping' }));
    isConnected = yield call(isSocketConnected);
  }
}

function* keepAlive() {
  const pingTask: Task = yield fork(ping);
  yield take(actionTypes.CLOSED);
  yield cancel(pingTask);
}

export default function* socketMiddleware() {
  yield takeEvery(subscriberAction, subscribeChannelWorker);
  yield takeEvery(unsubscriberAction, unsubscribeChannelWorker);

  yield takeLatest(actionTypes.SEND, sendMessage);
  yield takeLatest(actionTypes.CONNECTED, keepAlive);
  yield takeLatest(actionTypes.CONNECT, initialize);
}
